import React, { useEffect, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import axiosApiInstance from "../../utils/axios";
import { brandRoutes } from "../../constants";
import useAuth from "../../hooks/useAuth";
import { BrandContext } from "./context";

export const BrandState = ({ children }) => {
  const location = useLocation();
  const { isBrandBudtender, isBrandManager, isAnyAdmin, isBrandDriver } =
    useAuth();
  const [brand, setBrand] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [brands, setBrands] = useState([]);
  // TODO transfer this useMemo block to useAuth()
  const isBrandUser = useMemo(
    () => isBrandBudtender || isBrandManager || isBrandDriver,
    [isBrandBudtender, isBrandManager, isBrandDriver]
  );

  const brandIsActivated = useMemo(() => {
    if (!brand?.activateDate) return null;
    return new Date() > new Date(brand.activateDate);
  }, [brand]);

  const isBrandRoute = useMemo(() => {
    let isBrandRouteTemp = false;
    [...brandRoutes].forEach((route) => {
      const match = matchPath(location.pathname, { path: route, exact: true });
      if (match) {
        isBrandRouteTemp = true;
      }
    });
    return isBrandRouteTemp;
  }, [location]);

  const getBrandByBrandUser = () => {
    axiosApiInstance
      .get(`/v2/brand/by-brand-user`, {
        headers: { common: { withoutLoading: true } },
      })
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setBrands(data);
        } else if (typeof data?.id !== "undefined") {
          setBrand(data);
        }
      });
  };

  const getBrandById = (brandId) => {
    axiosApiInstance
      .get(`/v2/brand/${brandId}`, {
        headers: { common: { withoutLoading: true } },
      })
      .then(({ data }) => {
        setBrand(data);
      });
  };

  const getBrand = () => {
    if (isBrandUser) {
      getBrandByBrandUser();
    } else {
      getBrandById(brandId);
    }
  };

  useEffect(() => {
    if (isBrandDriver) return;

    if (isAnyAdmin && !isBrandRoute) {
      setBrandId(null);
      setBrand(null);
      setBrands([]);
    }

    const brandIdTemp = isBrandRoute
      ? Number(
          location.pathname.replace(/\/brand\//, "").replace(/\/[\w-]*/g, "")
        )
      : null;
    setBrandId(brandIdTemp);
  }, [location, isBrandRoute, isAnyAdmin, isBrandDriver]);

  useEffect(() => {
    if (isBrandUser) getBrand();
    else if (!isBrandUser && !!brandId) getBrand();
  }, [isBrandUser]);

  // TODO: WDF is this? Why is this here? This is not a good practice
  useEffect(() => {
    if (!!brandId && brands.length === 0 && !brand) {
      getBrand();
    } else if (brands.length > 0 && !brandId) {
      setBrandId(brands[0].id);
      setBrand(brands[0]);
    } else if (brandId !== brand?.id) {
      const newBrand = brands.find(({ id }) => id === brandId);
      if (newBrand) setBrand(newBrand);
    }
  }, [brandId, brand, brands]);

  const value = useMemo(
    () => ({
      isBrandRoute,
      brand,
      brandId,
      getBrandByBrandUser,
      getBrandById,
      getBrand,
      isBrandUser,
      brands,
      brandIsActivated,
    }),
    // eslint-disable-next-line
    [isBrandRoute, brand, brandId, isBrandUser, brands, brandIsActivated]
  );

  return (
    <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
  );
};
